import { useLazyQuery, useMutation } from "@apollo/client";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import HELP_ALL_TOPICS_AND_ARTICLES from "../../graphql/queries/HelpContext/allTopicsAndArticles";
import CREATE_HELP_TOPIC from "../../graphql/mutations/HelpContext/createTopic";
import DELETE_HELP_ARTICLE from "../../graphql/mutations/HelpContext/deleteArticle";
import DELETE_HELP_TOPIC from "../../graphql/mutations/HelpContext/deleteTopic";
import MOVE_HELP_ARTICLE from "../../graphql/mutations/HelpContext/moveArticle";
import MOVE_HELP_TOPIC from "../../graphql/mutations/HelpContext/moveTopic";
import UPDATE_HELP_ARTICLE from "../../graphql/mutations/HelpContext/updateArticle";
import UPDATE_HELP_TOPIC from "../../graphql/mutations/HelpContext/updateTopic";
import { useHelpCenterRights } from "../../hooks/useHelpCenterRights";
import { AuthContext } from "../../providers/authProvider";

export const helpContext = createContext();

const { Provider } = helpContext;

const groupBy = (array, keyFn) => {
  return array.reduce((acc, item) => {
    const key = keyFn(item) ?? null;
    (acc[key] ||= []).push(item);
    return acc;
  }, {});
};

const buildTree = (topics, articles) => {
  if (!Array.isArray(topics) && !Array.isArray(articles)) {
    return [];
  }

  const topicsByParentId = groupBy(topics, n => n.parentTopicId);
  const articlesByTopicId = groupBy(articles, n => n.topicId);

  const updateTopic = (topic) => {
    const topicId = topic.topicId;

    topic.id = `topic_${topicId}`;
    topic.type = 'topic';
    topic.children = [];

    let childTopics = topicsByParentId[topicId];
    let childArticles = articlesByTopicId[topicId];

    if (childTopics || childArticles) {
      childTopics?.forEach(n => topic.children.push(updateTopic(n)));
      childArticles?.forEach(n => topic.children.push(updateArticle(n)));
    }

    return topic;
  };

  const updateArticle = (article) => {
    const articleId = article.articleId;

    article.id = `article_${articleId}`;
    article.type = 'article';

    return article;
  };

  topicsByParentId[null]?.forEach(updateTopic);
  articlesByTopicId[null]?.forEach(updateArticle);

  return [
    ...(topicsByParentId[null] || []),
    ...(articlesByTopicId[null] || []),
  ];
};

const HelpProvider = ({ children }) => {

  const { isAuthenticated } = useContext(AuthContext);

  const { canEdit } = useHelpCenterRights();

  const [allArticles, setAllArticles] = useState([]);
  const [allTopics, setAllTopics] = useState([]);

  const [getAllTopicsAndArticles, { data, loading, error }] = useLazyQuery(HELP_ALL_TOPICS_AND_ARTICLES);

  const [createTopicMutation, { loading: createTopicLoading }] = useMutation(CREATE_HELP_TOPIC);
  const [deleteArticleMutation, { loading: deleteArticleLoading }] = useMutation(DELETE_HELP_ARTICLE);
  const [deleteTopicMutation, { loading: deleteTopicLoading }] = useMutation(DELETE_HELP_TOPIC);
  const [moveArticleMutation, { loading: moveArticleLoading }] = useMutation(MOVE_HELP_ARTICLE);
  const [moveTopicMutation, { loading: moveTopicLoading }] = useMutation(MOVE_HELP_TOPIC);
  const [updateArticleMutation, { loading: updateArticleLoading }] = useMutation(UPDATE_HELP_ARTICLE);
  const [updateTopicMutation, { loading: updateTopicLoading }] = useMutation(UPDATE_HELP_TOPIC);

  useEffect(() => {
    if (isAuthenticated()) {
      getAllTopicsAndArticles();
    }
  }, [isAuthenticated, getAllTopicsAndArticles]);

  useEffect(() => {
    if (data?.helpContext?.allArticles) {
      setAllArticles(data.helpContext.allArticles);
    }
    if (data?.helpContext?.allTopics) {
      setAllTopics(data.helpContext.allTopics);
    }
  }, [data]);

  const treeData = useMemo(() => (
    buildTree(allTopics, allArticles)
  ), [allArticles, allTopics, buildTree]);

  const createArticle = useCallback(
    async (articleInput) => {
      try {
        const response = await updateArticleMutation({ variables: { input: articleInput } });

        const created = response?.data?.helpContext?.updateHelpArticle;

        if (!created) {
          console.error("** Failed to create article");
          return;
        }

        created.articleId = created.id;
        created.id = `article_${created.id}`;

        setAllArticles((prev) => {
          var topicIndex = prev.findIndex(node => node.topicId === created.topicId);
          if (topicIndex === -1) {
            return [...prev, created];
          }

          let groupIndex = -1;

          var insertIndex = prev.findIndex((node, index) => {
            if (node.topicId === created.topicId) {
              return (++groupIndex === created.index);
            }
            return false;
          });

          if (insertIndex === -1) {
            insertIndex = topicIndex + created.index;
          }

          var newArticles = [...prev];

          newArticles.splice(insertIndex, 0, created);

          return newArticles
        });

        return created;
      } catch (err) {
        console.error("** Failed to create article", err);
      }
    },
    [updateArticleMutation]
  );

  const deleteArticle = useCallback(
    async (articleId) => {
      try {
        const response = await deleteArticleMutation({ variables: { articleId } });

        const result = response?.data?.helpContext?.deleteHelpArticle;

        if (!result || !result.success) {
          console.error("** Failed to delete article", result?.message);
          return;
        }

        setAllArticles(prev => {
          return prev.filter(n => n.articleId !== articleId);
        });
      } catch (err) {
        console.error("** Failed to delete topic", err);
      }
    },
    [deleteArticleMutation]
  );

  const updateArticle = useCallback(
    async (articleInput) => {
      try {
        const response = await updateArticleMutation({ variables: { input: articleInput } });

        const updated = response?.data?.helpContext?.updateHelpArticle;

        if (!updated) {
          console.error("** Failed to update article");
          return;
        }

        updated.articleId = updated.id;
        updated.id = `article_${updated.id}`;

        setAllArticles((prev) => {
          const exists = prev.some((a) => a.id === updated.id);
          return exists
            ? prev.map((a) => (a.id === updated.id ? updated : a))
            : [...prev, updated];
        });

        return updated;
      } catch (err) {
        console.error("** Failed to update article", err);
      }
    },
    [updateArticleMutation]
  );

  const createTopic = useCallback(
    async (topicInput) => {
      try {
        const response = await createTopicMutation({ variables: { input: topicInput } });

        const result = response?.data?.helpContext?.createHelpTopic;

        if (!result || !result.success) {
          console.error("** Failed to create topic", result?.message);
          return;
        }

        const created = result.topic;

        created.id = `topic_${created.topicId}`;

        setAllTopics((prev) => {
          var topicIndex = prev.findIndex(node => node.parentTopicId === created.parentTopicId);
          if (topicIndex === -1) {
            return [...prev, created];
          }

          let groupIndex = -1;

          var insertIndex = prev.findIndex((node, index) => {
            if (node.parentTopicId === created.parentTopicId) {
              return (++groupIndex === created.index);
            }
            return false;
          });

          if (insertIndex === -1) {
            insertIndex = topicIndex + created.index;
          }

          var newTopics = [...prev];

          newTopics.splice(insertIndex, 0, created);

          return newTopics
        });

        return created;
      } catch (err) {
        console.error("** Failed to create topic", err);
      }
    },
    [createTopicMutation]
  );

  const removeTopic = useCallback(
    (topicId) => {
      const theTopic = allTopics.find(n => n.topicId === topicId);
      if (!theTopic) {
        return;
      }

      const parentTopicId = theTopic.parentTopicId;

      setAllTopics(prev => {
        let start = [];
        let middle = [];
        let end = [];

        let found = false;

        prev.forEach(n => {
          if (n.topicId === topicId) {
            found = true;
          }
          else if (n.parentTopicId === topicId) {
            middle.push({ ...n, parentTopicId });
          }
          else {
            (found ? end : start).push(n);
          }
        });

        return [...start, ...middle, ...end];
      });

      setAllArticles(prev => {
        let start = [];
        let end = [];

        let found = false;

        prev.forEach(n => {
          if (n.topicId === parentTopicId) {
            found = true;
            end.push(n);
          }
          else if (n.topicId === topicId) {
            start.push({ ...n, topicId: parentTopicId });
          }
          else {
            (found ? end : start).push(n);
          }
        });

        return [...start, ...end];
      });
    },
    [allArticles, allTopics]
  );

  const deleteTopic = useCallback(
    async (topicId) => {
      try {
        const response = await deleteTopicMutation({ variables: { topicId } });

        const result = response?.data?.helpContext?.deleteHelpTopic;

        if (!result || !result.success) {
          console.error("** Failed to delete topic", result?.message);
          return;
        }

        removeTopic(result.topicId);
      } catch (err) {
        console.error("** Failed to delete topic", err);
      }
    },
    [deleteTopicMutation, removeTopic]
  );

  const updateTopic = useCallback(
    async (topicInput) => {
      try {
        const response = await updateTopicMutation({ variables: { input: topicInput } });

        const result = response?.data?.helpContext?.updateHelpTopic;

        if (!result || !result.success) {
          console.error("** Failed to update topic", result?.message);
          return;
        }

        const updated = result.topic;

        updated.id = `topic_${updated.topicId}`;

        setAllTopics((prev) => {
          const exists = prev.some((a) => a.id === updated.id);
          return exists
            ? prev.map((a) => (a.id === updated.id ? updated : a))
            : [...prev, updated];
        });

        return updated;
      } catch (err) {
        console.error("** Failed to update topic", err);
      }
    },
    [updateTopicMutation]
  );

  const moveArticle = useCallback(
    async (articleInput) => {
      try {
        const { articleId, parentTopicId, index } = articleInput;

        const response = await moveArticleMutation({ variables: { input: { id: articleId, parentId: parentTopicId || null, index } } });
        
        const result = response?.data?.helpContext?.moveHelpArticle;
        
        if (!result || !result.success) {
          console.error("** Failed to move article", result?.message);
          return;
        }
        
        const moved = result.topic;

        setAllArticles(prev => {
          let start = [], end = [];
          let articleFound = false, targetFound = false;
          let theNode;
          let groupIndex = -1, prevTopicId = -1;

          const handleTargetIsLastElementOfGroup = () => {
            if ((prevTopicId === (parentTopicId || null)) && (index === groupIndex + 1)) {
              targetFound = true;
              if (articleFound) {
                start.push(theNode);
              }
            }
          };

          prev.forEach(n => {
            if (n.topicId === prevTopicId) {
              groupIndex++;
            }
            else {
              handleTargetIsLastElementOfGroup();
              groupIndex = 0;
              prevTopicId = n.topicId;
            }

            if (!articleFound && (n.articleId === articleId)) {
              articleFound = true;
              theNode = { ...n, topicId: parentTopicId || null };
              if (targetFound) {
                start.push(theNode);
              }
            }
            else {
              if (!targetFound && (n.topicId === (parentTopicId || null)) && (index === groupIndex)) {
                targetFound = true;
                if (articleFound) {
                  start.push(theNode);
                }
              }

              (targetFound ? end : start).push(n);
            }
          });

          handleTargetIsLastElementOfGroup();

          if (!targetFound) {
            end.push(theNode);
          }

          return [...start, ...end];
        });

        return moved;
      } catch (err) {
        console.error("** Failed to move article", err);
      }
    },
    [moveArticleMutation]
  );

  const moveTopic = useCallback(
    async (topicInput) => {
      try {
        const { topicId, parentTopicId, index } = topicInput;

        const response = await moveTopicMutation({ variables: { input: { id: topicId, parentId: parentTopicId || null, index } } });
        
        const result = response?.data?.helpContext?.moveHelpTopic;
        
        if (!result || !result.success) {
          console.error("** Failed to move topic", result?.message);
          return;
        }
        
        const moved = result.topic;

        setAllTopics(prev => {
          let start = [], end = [];
          let targetFound = false, topicFound = false;
          let theNode;
          let groupIndex = -1, prevTopicId = -1

          const handleTargetIsLastElementOfGroup = () => {
            if ((prevTopicId === (parentTopicId || null)) && (index === groupIndex + 1)) {
              targetFound = true;
              if (topicFound) {
                start.push(theNode);
              }
            }
          };

          prev.forEach(n => {
            if (n.parentTopicId === prevTopicId) {
              groupIndex++;
            }
            else {
              handleTargetIsLastElementOfGroup();
              groupIndex = 0;
              prevTopicId = n.parentTopicId;
            }

            if (!topicFound && (n.topicId === topicId)) {
              topicFound = true;
              theNode = { ...n, parentTopicId: parentTopicId || null };
              if (targetFound) {
                start.push(theNode);
              }
            }
            else {
              if (!targetFound && (n.parentTopicId === (parentTopicId || null)) && (index === groupIndex)) {
                targetFound = true;
                if (topicFound) {
                  start.push(theNode);
                }
              }

              (targetFound ? end : start).push(n);
            }
          });

          handleTargetIsLastElementOfGroup();

          if (!targetFound) {
            end.push(theNode);
          }

          return [...start, ...end];
        });

        return moved;
      } catch (err) {
        console.error("** Failed to move topic", err);
      }
    },
    [moveTopicMutation]
  );

  const state = {
    allArticles,
    allTopics,
    loading,
    error,
    treeData,
    createArticle,
    createArticleLoading: updateArticleLoading,

    deleteArticle,
    deleteTopic,

    moveArticle,
    moveTopic,

    updateArticle,
    updateArticleLoading,
    createTopic,
    createTopicLoading,
    updateTopic,
    updateTopicLoading,
  };

  return <Provider value={state}>{children}</Provider>;
};

export default HelpProvider;
